/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';

import { useQuery } from '@apollo/client/react';
import {
  IconBell,
  IconFileText,
  IconHome,
  IconLifebuoy,
  IconLink,
  IconReportAnalytics,
  IconUsers,
} from '@tabler/icons-react';
import { Navigate, Route, Routes, useLocation } from 'react-router';

import BuyerAppointmentsSection from '../../components/BuyerAppointmentsSection';
import ContactsPanel from '../../components/ContactsPanel';
import CustomersSection from '../../components/CustomersSection';
import DocumentsSection from '../../components/DocumentsSection';
import FaqSection from '../../components/FaqSection';
import { FullPageHomeLoader } from '../../components/FullPageHomeLoader';
import { Layout } from '../../components/Layout';
import LearningCenterSection from '../../components/LearningCenterSection';
import NotificationPreferences from '../../components/NotificationPreferences';
import { ProductEligibilitySection } from '../../components/ProductEligibilitySection';
import ReferCustomersSection from '../../components/ReferCustomersSection';
import { SidebarNav, SidebarNavItem } from '../../components/SidebarNav';
import { GetReferralDashboardDataDocument } from '../../graphql/queries/__generated__/GetReferralDashboardData';
import type { GetReferralDashboardData } from '../../graphql/queries/__generated__/GetReferralDashboardData';

type GetReferralDashboardData_organization = NonNullable<GetReferralDashboardData['organization']>;
import { ERROR_PAGE_ROUTE } from '../routes';
import { FAQS } from './faqs';

const BASE_PATH = '/referral-dashboard';
export const REFERRAL_DASHBOARD_NAME = 'referral dashboard';

interface ITab extends SidebarNavItem {
  component: React.ComponentType;
  path: string;
}

const renderSidePanel = () => <ContactsPanel />;

const DashboardRoutes: React.FC<{ tabs: Array<ITab> }> = ({ tabs }) => (
  <Routes>
    <Route index element={<Navigate to={tabs[0].link} replace />} />
    {tabs.map((tab) => (
      <Route key={tab.link} path={tab.path} element={<tab.component />} />
    ))}
  </Routes>
);

const DashboardSidebar: React.FC<{ tabs: Array<ITab>; currentPath: string }> = ({
  tabs,
  currentPath,
}) => {
  const tabsWithActiveState = tabs.map((tab) => ({
    ...tab,
    isActive: currentPath === tab.link,
  }));
  return <SidebarNav navBarItems={tabsWithActiveState} />;
};

const ReferralDashboard: React.FC = () => {
  const location = useLocation();
  const { loading, error, data } = useQuery(GetReferralDashboardDataDocument);

  let renderSide;

  if (error) {
    return <Navigate to={ERROR_PAGE_ROUTE} replace />;
  }

  if (data && data.organization!.partnershipType === 'HOME_BUILDER') {
    renderSide = renderSidePanel;
  }

  let links: Array<ITab> = [
    {
      name: 'Referral Link',
      icon: <IconLink size={24} />,
      link: BASE_PATH + '/referral-link',
      path: 'referral-link',
      component: () => (
        <ReferCustomersSection
          organization={data!.organization! as GetReferralDashboardData_organization}
        />
      ),
    },
    {
      name: 'Home Eligibility',
      icon: <IconHome size={24} />,
      link: BASE_PATH + '/home-eligibility',
      path: 'home-eligibility',
      component: () => (
        <ProductEligibilitySection
          organization={data!.organization! as GetReferralDashboardData_organization}
        />
      ),
    },
    {
      name: 'Opendoor Customers',
      icon: <IconReportAnalytics size={24} />,
      link: BASE_PATH + '/customers',
      path: 'customers',
      component: () => <CustomersSection />,
    },
  ];

  // Only a subset of homebuilder partners are part of the pilot.
  if (data?.organization?.inPartnerBuyerLeadPilot) {
    links = links.concat([
      {
        name: 'Buyer Appointments',
        icon: <IconHome size={24} />,
        link: BASE_PATH + '/buyer-appointments',
        path: 'buyer-appointments',
        component: () => <BuyerAppointmentsSection />,
      },
    ]);
  }

  links = links.concat({
    name: 'Email Preferences',
    icon: <IconBell size={24} />,
    link: BASE_PATH + '/subscription-preferences',
    path: 'subscription-preferences',
    component: NotificationPreferences,
  });

  links = links.concat([
    {
      name: 'FAQs',
      icon: <IconLifebuoy size={24} />,
      link: BASE_PATH + '/faqs',
      path: 'faqs',
      component: () => <FaqSection faqs={FAQS} />,
    },
  ]);

  // These sections are only displayed to homebuilder users, so tabs need to hide as well
  if (data?.organization?.partnershipType === 'HOME_BUILDER') {
    links = links.concat(
      {
        name: 'Documents',
        icon: <IconFileText size={24} />,
        link: BASE_PATH + '/documents',
        path: 'documents',
        component: DocumentsSection,
      },
      {
        name: 'Learning Center',
        icon: <IconUsers size={24} />,
        link: BASE_PATH + '/learning-center',
        path: 'learning-center',
        component: LearningCenterSection,
      },
    );
  }

  return (
    <Layout
      renderContent={
        loading ? () => <FullPageHomeLoader /> : () => <DashboardRoutes tabs={links} />
      }
      renderNav={() => <DashboardSidebar tabs={links} currentPath={location.pathname} />}
      renderSide={renderSide}
    />
  );
};

// React.memo maintains parity with the original PureComponent implementation.
// While it won't prevent re-renders during navigation (which are necessary),
// it prevents unnecessary re-renders from parent component changes.
export default React.memo(ReferralDashboard);
